import { makeStyles } from "@fluentui/react-components"
import type { MetricSeriesPoint } from "@rema/shared/schemas/metrics"
import { BarChart, ScatterChart } from "echarts/charts"
import { GridComponent, TooltipComponent } from "echarts/components"
import * as echarts from "echarts/core"
import { CanvasRenderer } from "echarts/renderers"
import { useEffect, useRef } from "react"

// Registro tree-shaken de ECharts: barras (histograma) + scatter (puntos de cubo
// vacío) + grid + tooltip + canvas. Evita arrastrar el bundle completo de echarts.
echarts.use([BarChart, ScatterChart, GridComponent, TooltipComponent, CanvasRenderer])

const useStyles = makeStyles({
  chart: { width: "100%", height: "120px" },
})

// HH:MM para series por minuto; HH:MM:SS para series por segundo (RPS).
const formatTime = (iso: string, intervalSeconds: number): string => {
  const d = new Date(iso)
  return intervalSeconds <= 1
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

interface TooltipParam {
  axisValue: string
  value: number
}

export interface EChartsHistogramProps {
  points: MetricSeriesPoint[]
  intervalSeconds: number
  color: string
  dark: boolean
  // Sufijo de unidad para el tooltip (p. ej. "req/s", "/min").
  unit?: string
}

// Histograma (barras) de una serie temporal, pensado para refrescarse cada pocos
// segundos sin parpadeo (animation off). La base lleva SIEMPRE una línea punteada y
// cada cubo en 0 (o sin datos) muestra un punto vacío sobre ella, para que un gráfico
// sin actividad se lea como una línea de puntos y no como un hueco en blanco.
export const EChartsHistogram = ({
  points,
  intervalSeconds,
  color,
  dark,
  unit,
}: EChartsHistogramProps) => {
  const styles = useStyles()
  const ref = useRef<HTMLDivElement | null>(null)
  const chartRef = useRef<ReturnType<typeof echarts.init> | null>(null)

  // Init + dispose + resize una sola vez por montaje.
  useEffect(() => {
    if (!ref.current) return
    const chart = echarts.init(ref.current, undefined, { renderer: "canvas" })
    chartRef.current = chart
    const observed = ref.current
    const ro = new ResizeObserver(() => chart.resize())
    ro.observe(observed)
    return () => {
      ro.disconnect()
      chart.dispose()
      chartRef.current = null
    }
  }, [])

  // Re-pinta cuando cambian datos o tema.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    // Sin datos (p. ej. Redis caído): se sintetiza una ventana plana en 0 para que la
    // línea punteada y los puntos vacíos se vean igualmente.
    const data: MetricSeriesPoint[] =
      points.length > 0
        ? points
        : Array.from({ length: 30 }, (_, i) => ({
            ts: new Date(Date.now() - (30 - i) * intervalSeconds * 1000).toISOString(),
            value: 0,
          }))
    const labels = data.map((p) => formatTime(p.ts, intervalSeconds))
    const baseline = dark ? "#5a5a5a" : "#c7c7c7"
    const dot = dark ? "#7a7a7a" : "#bdbdbd"
    chart.setOption(
      {
        animation: false,
        grid: { left: 4, right: 4, top: 12, bottom: 8, containLabel: false },
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          backgroundColor: dark ? "#292929" : "#ffffff",
          borderColor: dark ? "#3d3d3d" : "#e0e0e0",
          textStyle: { color: dark ? "#ffffff" : "#242424", fontSize: 12 },
          formatter: (params: TooltipParam[]) => {
            const p = params[0]
            if (!p) return ""
            const v = unit ? `${p.value} ${unit}` : String(p.value)
            return `${p.axisValue}<br/><b>${v}</b>`
          },
        },
        xAxis: {
          type: "category",
          data: labels,
          boundaryGap: true,
          // Línea punteada de base, siempre visible.
          axisLine: { show: true, lineStyle: { type: "dashed", color: baseline, width: 1 } },
          axisTick: { show: false },
          axisLabel: { show: false },
        },
        yAxis: { type: "value", show: false, min: 0 },
        series: [
          {
            type: "bar",
            data: data.map((p) => p.value),
            itemStyle: { color, borderRadius: [2, 2, 0, 0] },
            barCategoryGap: "18%",
            z: 2,
          },
          {
            // Punto vacío (círculo hueco) en cada cubo que está en 0 / sin datos.
            type: "scatter",
            symbol: "circle",
            symbolSize: 5,
            clip: false,
            silent: true,
            z: 3,
            data: data.map((p) => (p.value === 0 ? 0 : null)),
            itemStyle: { color: "transparent", borderColor: dot, borderWidth: 1 },
          },
        ],
      },
      { notMerge: true },
    )
  }, [points, intervalSeconds, color, dark, unit])

  return <div ref={ref} className={styles.chart} />
}
